import React, { useState, useMemo, useCallback } from "react";
import RIGHT from "../../Assets/site-audit/right.png";
import WRONG from "../../Assets/site-audit/wrong2.png";
import ToolTip from "../components/tooltip";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { getStatusCodeColor } from "../../../helperJs/helper.js";

import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import VirtualizeTable from "./DynamicTable/VirtualizeTable.jsx";
import { Paper } from "@mui/material";
import Tooltip from "../components/tooltip";
import { ReactComponent as UnsortIcon } from "../../Assets/rank-tracking-assets/unshort.svg";
import GetSeverity from "../helper-js/getSeverity.js";
import { useTranslation } from "react-i18next";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";

function AuditTable({ tableHeader, tableData, type, showTitle }) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [showTable, setShowTable] = useState([]);
  const [severity, setSeverity] = useState(0);
  const { t } = useTranslation();

  const handleChange = (event, newValue) => {
    setSeverity(newValue);
  };

  const tableRows = useMemo(() => {
    if (!tableData) return [];

    if (tableHeader?.[0] !== "Top SEO Issues") return tableData;

    if (severity === 0) return tableData;

    const severityLevels = {
      1: "high",
      2: "medium",
      3: "low",
    };
    return severityLevels[severity]
      ? tableData.filter((item) => item.severity === severityLevels[severity])
      : tableData;
  }, [tableData, tableHeader, severity]);

  const showTableHandler = (e) => {
    if (typeof e !== "string") {
      if (showTable?.length === 0 || e !== showTable) {
        setShowTable(e);
      } else {
        setShowTable([]);
      }
    }
  };

  const sortRows = (rows, config) => {
    if (!rows || rows.length === 0) return [];
    if (!config?.key) return rows; // Return original rows if no sorting key is provided

    return [...rows].sort((a, b) => {
      let aValue = a[config.key];
      let bValue = b[config.key];

      // Handle "Pending" cases: Always move to the last position
      const isANoValue = aValue === "No Content";
      const isBNoValue = bValue === "No Content";

      if (isANoValue && isBNoValue) return 0;
      if (isANoValue) return 1;
      if (isBNoValue) return -1;

      // Handle undefined values by pushing them to the end
      if (aValue === undefined) return 1;
      if (bValue === undefined) return -1;

      // Normalize values for comparison (e.g., case-insensitive for strings)
      const normalizedA =
        typeof aValue === "string" ? aValue.toLowerCase() : aValue;
      const normalizedB =
        typeof bValue === "string" ? bValue.toLowerCase() : bValue;

      // Ascending or descending sorting
      if (config.direction === "asc") {
        return normalizedA > normalizedB ? 1 : -1;
      } else {
        return normalizedA < normalizedB ? 1 : -1;
      }
    });
  };

  const sortedTableData = useMemo(
    () => sortRows(tableRows, sortConfig),
    [tableRows, sortConfig]
  );

  const requestSort = useCallback(
    (key) => {
      let direction = "asc";
      if (sortConfig.key === key && sortConfig.direction === "asc") {
        direction = "desc";
      }
      setSortConfig({ key, direction });
    },
    [sortConfig]
  );

  const getSortIcon = () => {
    return <UnsortIcon className="ms-1" />;
  };

  const column = useMemo(() => {
    const sortKey = [
      {
        label: "URL Length",
        key: "length",
      },
      {
        label: "Word Count",
        key: "count",
      },
      {
        label: "Length",
        key: "length",
      },
      {
        label: "Load Time",
        key: "time",
      },
      {
        label: "No. of H1 Tags",
        key: "no_of_h1_tags",
      },
    ];

    return tableHeader.map((item, index) => {
      const sortingKey = sortKey?.find((key) => key.label === item);
      return {
        id: index,
        label: item,
        key: item,
        sortType: sortingKey ? sortingKey.key : false,
        align: index === 0 ? "left" : "center",
      };
    });
  }, [tableHeader]);

  function fixedHeaderContent() {
    return (
      <TableRow>
        {column?.map((header, index) => {
          return (
            <TableCell
              variant="head"
              align={header.align}
              key={`table-header-${index}`}
              sx={{
                fontSize: "0.9em",
                color: "#191B23",
                backgroundColor: "#F4F5F9",
                width: [
                  "Characters Check",
                  "Keyword Check",
                  "Dynamic Check",
                ].includes(header.label)
                  ? "200px"
                  : ["HTTP Status", "Length"].includes(header.label) &&
                    tableHeader?.length > 2
                  ? "160px"
                  : "auto",
              }}
              className={
                header.label === "Description"
                  ? "table-fixed-th font-medium px-3 py-2"
                  : "font-medium px-3 py-2"
              }
            >
              <span
                className={header.sortType ? "point" : ""}
                style={{
                  justifyContent: ["Description", "Title"].includes(
                    header.label
                  )
                    ? "flex-start"
                    : "center",
                }}
                onClick={() =>
                  header.sortType ? requestSort(header.sortType) : {}
                }
              >
                {header.label}
                {header.sortType && getSortIcon()}
              </span>
              {header.tooltip && <Tooltip title={header.tooltip} />}
            </TableCell>
          );
        })}
      </TableRow>
    );
  }

  function rowContent(_index, item) {
    return (
      <React.Fragment key={`table-row-${_index}`}>
        {item?.title && showTitle && !item?.status_code ? (
          <TableCell className="text-start font-medium px-3 py-2">
            {item?.title}
          </TableCell>
        ) : null}
        {item?.url ? (
          <TableCell
            onClick={() => showTableHandler(item.url)}
            className={
              type === "poorly_formatted"
                ? "length-url-lmt font-medium px-3 py-2"
                : "font-medium px-3 py-2"
            }
          >
            {typeof item.url === "string" ? (
              <div className="text-max-lmt">
                {!showTitle && (
                  <h1 className="heading-small font-regular mb-0 text-wrap">
                    {item?.title}
                  </h1>
                )}
                <span className="d-flex align-items-center">
                  <a
                    href={item.url === "undefined" ? "#" : item.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      alignItems: "center",
                      width: "100%",
                      color: !showTitle ? "#365486" : "rgba(0, 0, 0, 0.8)",
                    }}
                    className={`d-inline-flex ${
                      !showTitle ? "text-max-lmt" : ""
                    }`}
                  >
                    <ToolTip title={item?.url}>
                      <span
                        className="length-url-lmt"
                        style={{
                          color: "#365486",
                          maxWidth: "95%",
                        }}
                      >
                        {item?.url}
                      </span>
                    </ToolTip>
                    <OpenInNewIcon
                      sx={{
                        fontSize: "15px",
                        marginLeft: "2px",
                        color: "#365486 !important",
                      }}
                    />
                  </a>
                </span>
              </div>
            ) : item.url?.length === 1 ? (
              <a
                href={item.url === "undefined" ? "#" : item.url}
                target="_blank"
                rel="noreferrer"
                style={{
                  color: !showTitle ? "#365486" : "rgba(0, 0, 0, 0.8)",
                }}
                className={
                  type === "poorly_formatted" ? "w-100 d-inline-flex" : ""
                }
              >
                {" "}
                {type === "poorly_formatted" ? (
                  <ToolTip title={item?.url[0]}>
                    <span
                      className="length-url-lmt"
                      style={{ maxWidth: "100%" }}
                    >
                      {item?.url[0]}{" "}
                    </span>
                  </ToolTip>
                ) : (
                  item?.url[0]
                )}
              </a>
            ) : (
              <button
                className="site-audit-url py-1 px-2"
                style={{
                  background: "#fff",
                  color: "rgb(233, 86, 85)",
                  width: "100px",
                }}
              >
                {item.url.length}{" "}
                <span className="mx-1">
                  {t("site_audit.dashboard_page.seo_issues.table_header.pages")}
                </span>
                <i
                  className="fa-solid fa-angle-down ms-2"
                  title="download Pdf"
                ></i>
              </button>
            )}
          </TableCell>
        ) : null}
        {item?.count ? (
          <TableCell
            className="text-center font-medium px-3 py-2"
            style={{ textTransform: "capitalize" }}
          >
            {item.count}
          </TableCell>
        ) : null}
        {item?.td1 ? (
          <TableCell
            className="point font-medium px-3 py-2"
            onClick={item?.td2?.length > 0 && item?.td3 ? item?.td3 : () => {}}
          >
            <span style={{ color: "#365486" }} className="font-light-bold">
              {item?.td2?.length}
              {t("site_audit.dashboard_page.seo_issues.table_header.pages")}
            </span>
            {item.td1}
            {<GetSeverity issuesType={item} />}
          </TableCell>
        ) : null}
        {item?.status_code ? (
          <TableCell className="text-center font-medium px-3 py-2">
            <span
              className="table-status-code px-3 py-1"
              style={{
                color: getStatusCodeColor(item.status_code)?.color,
                backgroundColor: getStatusCodeColor(item.status_code)
                  ?.backgroundColor,
                borderRadius: "3px",
              }}
            >
              {item.status_code}
            </span>
          </TableCell>
        ) : null}
        {item?.h1_tags ? (
          <TableCell className="font-medium px-3 py-2">
            {item.h1_tags}
          </TableCell>
        ) : null}
        {item?.desc ? (
          <TableCell className="text-start font-medium px-3 py-2">
            {item.desc}
          </TableCell>
        ) : null}
        {item?.title && showTitle && item?.status_code ? (
          <TableCell className="text-start font-medium px-3 py-2">
            {item.title}
          </TableCell>
        ) : null}
        {item?.no_of_h1_tags ? (
          <TableCell className="text-center font-medium px-3 py-2">
            {item.no_of_h1_tags}
          </TableCell>
        ) : null}
        {item?.length ? (
          <TableCell className="text-center font-medium px-3 py-2">
            {item.length}
          </TableCell>
        ) : null}
        {item?.td3 ? (
          <TableCell
            className="v_All text-center font-medium px-3 py-2"
            onClick={item?.td2?.length > 0 ? item.td3 : () => {}}
            style={{ height: "50px" }}
          >
            <span
              style={{
                backgroundColor: "#365486",
                color: "#FFFFFF",
                opacity: item?.td2?.length > 0 ? "1" : "0.5",
                padding: "7px 10px",
                borderRadius: "8px",
              }}
            >
              {t("common_buttons.view")}
            </span>
          </TableCell>
        ) : null}
        {item?.character_check ? (
          <TableCell className="font-medium px-3 py-2">
            {item.character_check}
          </TableCell>
        ) : null}
        {item?.characters ||
        typeof item.characters === "boolean" ||
        item?.characters === "undefined" ? (
          <TableCell className="text-center font-medium px-3 py-2">
            {item.characters === true ? (
              <>
                <img src={RIGHT} className="RIGHTIMG" alt="right-tick-icon" />
              </>
            ) : (
              <>
                <img src={WRONG} className="WRONGIMG" alt="wrong-tick-icon" />
              </>
            )}
          </TableCell>
        ) : null}
        {item?.dynamic ||
        typeof item.dynamic === "boolean" ||
        item?.dynamic === "undefined" ? (
          <TableCell className="text-center font-medium px-3 py-2">
            {item.dynamic === true ? (
              <>
                <img src={RIGHT} className="RIGHTIMG" alt="right-tick-icon" />
              </>
            ) : (
              <>
                <img src={WRONG} className="WRONGIMG" alt="wrong-tick-icon" />
              </>
            )}
          </TableCell>
        ) : null}
        {item?.keywords ||
        typeof item?.keywords === "boolean" ||
        item?.keywords === "undefined" ? (
          <TableCell className="text-center font-medium px-3 py-2">
            {item.keywords === true ? (
              <>
                <img src={RIGHT} className="RIGHTIMG" alt="right-tick-icon" />
              </>
            ) : (
              <>
                <img src={WRONG} className="WRONGIMG" alt="wrong-tick-icon" />
              </>
            )}
          </TableCell>
        ) : null}
        {item?.time ? (
          <TableCell className="text-center font-medium px-3 py-2">
            {item.time} sec
          </TableCell>
        ) : null}
        {item?.url === showTable
          ? showTable?.length > 0 &&
            showTable?.map((res, ind) => {
              return (
                <tr key={ind}>
                  <TableCell colSpan={2} className="font-medium px-3 py-2">
                    <div className="text-max-lmt w-100">
                      {item?.title ? <span>{item?.title}</span> : null}
                      <span className="d-flex align-items-center">
                        <a
                          href={res || "#"}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            alignItems: "center",
                            color: "#365486",
                          }}
                          className={"d-inline-flex text-max-lmt w-100"}
                        >
                          <ToolTip title={res || ""}>
                            <span
                              className="length-url-lmt"
                              style={{
                                color: "#365486",
                                maxWidth: "95%",
                              }}
                            >
                              {res || ""}
                            </span>
                          </ToolTip>
                          <OpenInNewIcon
                            sx={{
                              fontSize: "15px",
                              marginLeft: "2px",
                              color: "#365486 !important",
                            }}
                          />
                        </a>
                      </span>
                    </div>
                  </TableCell>
                </tr>
              );
            })
          : false}
      </React.Fragment>
    );
  }

  return (
    <>
      {tableHeader?.length > 0 && tableHeader?.[0] === "Top SEO Issues" && (
        <Box sx={{ width: "100%" }} className="my-3">
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={severity}
              onChange={handleChange}
              aria-label="basic tabs example"
            >
              <Tab
                label="All Issues"
                className="py-0 heading-small font-bold"
              />
              <Tab
                label="Critical Error"
                className="py-0 heading-small font-bold"
              />
              <Tab label="Moderate" className="py-0 heading-small font-bold" />
              <Tab label="Low" className="py-0 heading-small font-bold" />
            </Tabs>
          </Box>
        </Box>
      )}
      <Paper
        className={"w-100 mt-3 vir-table-border"}
        sx={{
          backgroundColor: "background.paper",
          boxShadow: "none",
          "& .MuiTableContainer-root": {
            boxShadow: "none",
            backgroundColor: "background.paper",
          },
        }}
      >
        <VirtualizeTable
          {...{
            rowContent,
            loading: false,
            fixedHeaderContent,
            columns: tableHeader,
            data: sortedTableData,
            columnLength: tableHeader?.length,
          }}
        />
      </Paper>
    </>
  );
}

export default AuditTable;
